import type { BlogPostMeta } from './types';
import { Project } from './projects';

export type SearchResult = {
  type: 'blog' | 'project';
  title: string;
  description: string;
  slug: string;
  tags: string[];
  contentMatch?: {
    text: string;
    position: number;
  }[];
};

export function search(query: string, blogs: BlogPostMeta[], projects: Project[]): SearchResult[] {
  const searchTerm = query.toLowerCase().trim();
  if (!searchTerm) return [];

  function findContentMatches(cleanContent: string, searchTerm: string) {
    const matches: { text: string; position: number }[] = [];
    const searchTermLower = searchTerm.toLowerCase();
    let position = cleanContent.toLowerCase().indexOf(searchTermLower);

    while (position !== -1 && matches.length < 3) {
      const start = Math.max(0, position - 30);
      const end = Math.min(cleanContent.length, position + searchTerm.length + 30);
      let text = cleanContent.slice(start, end);
      
      if (start > 0) text = '...' + text;
      if (end < cleanContent.length) text = text + '...';

      matches.push({ text, position: start });
      position = cleanContent.toLowerCase().indexOf(searchTermLower, position + 1);
    }

    return matches;
  }

  const blogResults = blogs.map(blog => ({
    type: 'blog' as const,
    title: blog.title,
    description: blog.excerpt,
    slug: blog.slug,
    tags: blog.tags,
    contentMatch: findContentMatches(blog.searchText, searchTerm)
  }));

  const projectResults = projects.map(project => ({
    type: 'project' as const,
    title: project.title,
    description: project.description,
    slug: project.slug,
    tags: project.tags.map(tag => tag.name)
  }));

  return [...blogResults, ...projectResults].filter(result => {
    const searchableText = [
      result.title,
      result.description,
      ...result.tags,
      ...(result.type === 'blog' && result.contentMatch ? 
        result.contentMatch.map(m => m.text) : [])
    ].join(' ').toLowerCase();

    return searchTerm.split(' ').every(term => 
      searchableText.includes(term)
    );
  });
}
